import React, { FC, useCallback, useEffect, useState } from "react";
import {
  EAcaoLogUser,
  EGrupoAcaoLogUser,
  ELogUserStatus,
  IAcompanhamento,
  IMatricula,
  ITurma,
} from "@deltasge/marauders-map";
import { Box, Tab, Tabs } from "@mui/material";
import { Filter as FilterIcon } from "mdi-material-ui";

import { api } from "configs";
import { gravaLogUser } from "configs/log";
import { useFilter } from "hooks/useFilter";
import { IFilterAcompanhamento } from "interfaces";
import { Main } from "pages/_layouts/Main";
import { useAppSelector } from "store";
import { TabPanel } from "components/TabPanel";
import { EmptyState } from "components/EmptyStates";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import {
  AnaliseIndividual,
  AnaliseTurma,
  ConselhoAluno,
  ConselhoTurma,
  Filter,
  Impressao,
} from "./components";

import "./styles.css";

const normalizarTexto = (texto?: string | null) =>
  texto && texto.trim().length > 0 ? texto : "";

export const AcompanhamentoPedagogico: FC = () => {
  const [matriculas, setMatriculas] = useState<IMatricula[]>([]);
  const [tab, setTab] = useState(0);

  const [coletivo, setColetivo] = useState<IAcompanhamento>();
  const [textoColetivo, setTextoColetivo] = useState("");

  const [registrosIndividuais, setRegistrosIndividuais] = useState<
    Record<number, IAcompanhamento>
  >({});
  const [textosIndividuais, setTextosIndividuais] = useState<
    Record<number, string>
  >({});

  const [pareceresTurma, setPareceresTurma] = useState<IAcompanhamento[]>([]);
  const [pareceresAluno, setPareceresAluno] = useState<IAcompanhamento[]>([]);

  const [loading, setLoading] = useState({
    coletivo: false,
    individuais: false,
    turma: false,
    aluno: false,
  });

  const [saving, setSaving] = useState(false);
  const [impressao, setImpressao] = useState<"turma" | "aluno">();

  const {
    escola,
    configuracoes: {
      configOnline: { configuracoes },
    },
    sistemaAvaliacao: { data: sistemaAvaliacao },
  } = useAppSelector((state) => state);

  const nomeEtapa = sistemaAvaliacao?.nomeEtapa || "Bimestre";

  const handleFilterCurso = (idCurso?: number) =>
    navigate({
      ...filter,
      idCurso,
      idSerie: undefined,
      idTurma: undefined,
      idMateria: undefined,
      idAluno: undefined,
    });

  const handleFilterSerie = (idSerie?: number) =>
    navigate({
      ...filter,
      idSerie,
      idTurma: undefined,
      idMateria: undefined,
      idAluno: undefined,
    });

  const handleFilterTurma = (idTurma?: number) =>
    navigate({
      ...filter,
      idTurma,
      idMateria: undefined,
      idAluno: undefined,
    });

  const handleFilterMateria = (idMateria?: number) =>
    navigate({
      ...filter,
      idMateria,
    });

  const handleFilterEtapa = (etapa?: number) =>
    navigate({
      ...filter,
      etapa: etapa ?? configuracoes.bim_site,
      idAluno: undefined,
    });

  const handleFilterMatricula = (idAluno?: number) =>
    navigate({ ...filter, idAluno });

  const {
    Component,
    navigate,
    filter,
    selecionados: { turma, materia },
  } = useFilter<IFilterAcompanhamento>({
    pathname: "/acompanhamento-pedagogico/",
    queryKeys: [
      "idCurso",
      "idSerie",
      "idTurma",
      "idMateria",
      "etapa",
      "idAluno",
    ],
    initialValue: {
      etapa: configuracoes.bim_site,
      anoLetivo: parseInt(escola.anoSite, 10),
    },
    options: {
      curso: {
        onClick: handleFilterCurso,
      },
      serie: {
        onClick: handleFilterSerie,
      },
      turma: {
        onClick: handleFilterTurma,
      },
      materia: {
        onClick: handleFilterMateria,
      },
      etapa: {
        onClick: handleFilterEtapa,
      },
    },
  });

  useEffect(() => {
    (async () => {
      try {
        if (!filter.idTurma) {
          setMatriculas([]);
          return;
        }
        const { data } = await api.get<ITurma | undefined>(
          `turma/${filter.idTurma}`,
          {
            cache: {
              ignoreCache: true,
            },
            params: {
              select: [
                "id",
                "matriculas.id",
                "matriculas.nrChamada",
                "matriculas.situacao",
                "matriculas.aluno.nome",
                "matriculas.aluno.nomeusual",
                "matriculas.aluno.uid",
                "matriculas.aluno.codigo",
                "matriculas.aluno.imagemAluno.caminhoArquivo",
              ],
              order: {
                matriculas: { nrChamada: "ASC" },
              },
            },
          }
        );
        if (data && Array.isArray(data.matriculas)) {
          setMatriculas(data.matriculas);
        } else {
          setMatriculas([]);
        }
      } catch (error) {
        snack.error(getError(error));
      }
    })();
  }, [filter.idTurma]);

  // Aba 0: parecer coletivo da turma na disciplina e etapa selecionadas
  useEffect(() => {
    let ativo = true;

    (async () => {
      if (tab !== 0 || !filter.idTurma || !filter.idMateria || !filter.etapa) {
        return;
      }
      try {
        setLoading((state) => ({ ...state, coletivo: true }));
        setColetivo(undefined);
        setTextoColetivo("");
        const { data } = await api.get<[IAcompanhamento[], number]>(
          "acompanhamento",
          {
            // sem cache: a chave ignora objeto aninhado (where) e repetiria a resposta anterior
            cache: { ignoreCache: true },
            params: {
              where: {
                idTurma: filter.idTurma,
                idMateria: filter.idMateria,
                etapa: filter.etapa,
                "idAluno.isnull": "",
              },
            },
          }
        );
        // resposta de um filtro que já mudou: descarta para não sobrepor a consulta atual
        if (!ativo) return;
        const registro =
          Array.isArray(data) && Array.isArray(data[0])
            ? data[0][0]
            : undefined;
        if (registro) {
          setColetivo(registro);
          setTextoColetivo(registro.texto ?? "");
        }
      } catch (error) {
        if (!ativo) return;
        snack.error(getError(error));
      } finally {
        if (ativo) setLoading((state) => ({ ...state, coletivo: false }));
      }
    })();

    return () => {
      ativo = false;
    };
  }, [tab, filter.idTurma, filter.idMateria, filter.etapa]);

  // Aba 1: pareceres individuais da disciplina e etapa selecionadas
  useEffect(() => {
    let ativo = true;

    (async () => {
      if (tab !== 1 || !filter.idTurma || !filter.idMateria || !filter.etapa) {
        return;
      }
      try {
        setLoading((state) => ({ ...state, individuais: true }));
        setRegistrosIndividuais({});
        setTextosIndividuais({});
        const { data } = await api.get<[IAcompanhamento[], number]>(
          "acompanhamento",
          {
            // sem cache: a chave ignora objeto aninhado (where) e repetiria a resposta anterior
            cache: { ignoreCache: true },
            params: {
              where: {
                idTurma: filter.idTurma,
                idMateria: filter.idMateria,
                etapa: filter.etapa,
                "idAluno.isnotnull": "",
              },
            },
          }
        );
        // resposta de um filtro que já mudou: descarta para não sobrepor a consulta atual
        if (!ativo) return;
        const registros =
          Array.isArray(data) && Array.isArray(data[0]) ? data[0] : [];
        const mapaRegistros: Record<number, IAcompanhamento> = {};
        const mapaTextos: Record<number, string> = {};
        registros.forEach((registro) => {
          if (registro.idAluno == undefined || registro.idAluno == null) return;
          mapaRegistros[registro.idAluno] = registro;
          mapaTextos[registro.idAluno] = registro.texto ?? "";
        });
        setRegistrosIndividuais(mapaRegistros);
        setTextosIndividuais(mapaTextos);
      } catch (error) {
        if (!ativo) return;
        snack.error(getError(error));
      } finally {
        if (ativo) setLoading((state) => ({ ...state, individuais: false }));
      }
    })();

    return () => {
      ativo = false;
    };
  }, [tab, filter.idTurma, filter.idMateria, filter.etapa]);

  // Aba 2: ata da turma (todas as análises coletivas da etapa)
  useEffect(() => {
    let ativo = true;

    (async () => {
      if (tab !== 2 || !filter.idTurma || !filter.etapa) {
        return;
      }
      try {
        setLoading((state) => ({ ...state, turma: true }));
        setPareceresTurma([]);
        const { data } = await api.get<[IAcompanhamento[], number]>(
          "acompanhamento",
          {
            // sem cache: a chave ignora objeto aninhado (where) e repetiria a resposta anterior
            cache: { ignoreCache: true },
            params: {
              where: {
                idTurma: filter.idTurma,
                etapa: filter.etapa,
                "idAluno.isnull": "",
              },
              select: ["id", "idMateria", "etapa", "texto", "materia.nome"],
            },
          }
        );
        // resposta de um filtro que já mudou: descarta para não sobrepor a consulta atual
        if (!ativo) return;
        setPareceresTurma(
          Array.isArray(data) && Array.isArray(data[0]) ? data[0] : []
        );
      } catch (error) {
        if (!ativo) return;
        snack.error(getError(error));
      } finally {
        if (ativo) setLoading((state) => ({ ...state, turma: false }));
      }
    })();

    return () => {
      ativo = false;
    };
  }, [tab, filter.idTurma, filter.etapa]);

  // Aba 3: relatório único por aluno (pareceres individuais da turma e etapa)
  useEffect(() => {
    let ativo = true;

    (async () => {
      if (tab !== 3 || !filter.idTurma || !filter.idAluno || !filter.etapa) {
        return;
      }
      try {
        setLoading((state) => ({ ...state, aluno: true }));
        setPareceresAluno([]);
        const { data } = await api.get<[IAcompanhamento[], number]>(
          "acompanhamento",
          {
            // sem cache: a chave ignora objeto aninhado (where) e repetiria a resposta anterior
            cache: { ignoreCache: true },
            params: {
              // ⚠️ Não mandar `"idAluno.isnotnull"` junto de `idAluno`: as duas chaves
              // escrevem na mesma propriedade no `generateWhere` do deltalizer (azkaban) e
              // o operador sobrescreve a igualdade, devolvendo o parecer de TODOS os alunos
              // da turma. `idAluno` sozinho já implica `id_aluno IS NOT NULL`.
              where: {
                idTurma: filter.idTurma,
                idAluno: filter.idAluno,
                etapa: filter.etapa,
              },
              select: ["id", "idMateria", "etapa", "texto", "materia.nome"],
            },
          }
        );
        // resposta de um filtro que já mudou: descarta para não sobrepor a consulta atual
        if (!ativo) return;
        setPareceresAluno(
          Array.isArray(data) && Array.isArray(data[0]) ? data[0] : []
        );
      } catch (error) {
        if (!ativo) return;
        snack.error(getError(error));
      } finally {
        if (ativo) setLoading((state) => ({ ...state, aluno: false }));
      }
    })();

    return () => {
      ativo = false;
    };
  }, [tab, filter.idTurma, filter.idAluno, filter.etapa]);

  const handleSaveColetivo = useCallback(async () => {
    if (!filter.idTurma || !filter.idMateria || !filter.etapa) return;
    try {
      setSaving(true);
      const params = {
        idTurma: filter.idTurma,
        idMateria: filter.idMateria,
        etapa: filter.etapa,
        texto: normalizarTexto(textoColetivo),
      };
      if (coletivo?.id) {
        const { data } = await api.put<IAcompanhamento>(
          `acompanhamento/${coletivo.id}`,
          params
        );
        setColetivo(data);
        gravaLogUser({
          logKey: {
            acao: EAcaoLogUser.Alteracao,
            grupoAcao: EGrupoAcaoLogUser.Nota,
          },
          logKeys: {
            idTurma: filter.idTurma,
            idMateria: filter.idMateria,
            etapa: filter.etapa,
          },
          status: ELogUserStatus.Aguardando,
          message: "alterou parecer coletivo",
          dataAfter: { ...params },
        });
      } else {
        const { data } = await api.post<IAcompanhamento>(
          "acompanhamento",
          params
        );
        setColetivo(data);
        gravaLogUser({
          logKey: {
            acao: EAcaoLogUser.Inclusao,
            grupoAcao: EGrupoAcaoLogUser.Nota,
          },
          logKeys: {
            idTurma: filter.idTurma,
            idMateria: filter.idMateria,
            etapa: filter.etapa,
          },
          status: ELogUserStatus.Aguardando,
          message: "incluiu parecer coletivo",
          dataAfter: { ...params },
        });
      }
      snack.success("Pareceres salvos com sucesso!");
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSaving(false);
    }
  }, [
    coletivo?.id,
    filter.idTurma,
    filter.idMateria,
    filter.etapa,
    textoColetivo,
  ]);

  const handleSaveIndividuais = useCallback(async () => {
    if (!filter.idTurma || !filter.idMateria || !filter.etapa) return;
    try {
      setSaving(true);
      const atualizados: Record<number, IAcompanhamento> = {
        ...registrosIndividuais,
      };
      for (const matricula of matriculas) {
        const idAluno = matricula.aluno?.uid;
        if (idAluno == undefined) continue;

        const texto = normalizarTexto(textosIndividuais[idAluno]);
        const registro = registrosIndividuais[idAluno];

        // Aluno sem texto e sem registro existente: nada é enviado
        if (!registro && texto.length === 0) continue;

        const params = {
          idTurma: filter.idTurma,
          idMateria: filter.idMateria,
          idAluno,
          etapa: filter.etapa,
          texto,
        };

        if (registro?.id) {
          const { data } = await api.put<IAcompanhamento>(
            `acompanhamento/${registro.id}`,
            params
          );
          atualizados[idAluno] = data;
          gravaLogUser({
            logKey: {
              acao: EAcaoLogUser.Alteracao,
              grupoAcao: EGrupoAcaoLogUser.Nota,
            },
            logKeys: {
              idTurma: filter.idTurma,
              idMateria: filter.idMateria,
              idAluno,
              etapa: filter.etapa,
            },
            status: ELogUserStatus.Aguardando,
            message: "alterou parecer do aluno",
            dataAfter: { ...params },
          });
        } else {
          const { data } = await api.post<IAcompanhamento>(
            "acompanhamento",
            params
          );
          atualizados[idAluno] = data;
          gravaLogUser({
            logKey: {
              acao: EAcaoLogUser.Inclusao,
              grupoAcao: EGrupoAcaoLogUser.Nota,
            },
            logKeys: {
              idTurma: filter.idTurma,
              idMateria: filter.idMateria,
              idAluno,
              etapa: filter.etapa,
            },
            status: ELogUserStatus.Aguardando,
            message: "incluiu parecer do aluno",
            dataAfter: { ...params },
          });
        }
      }
      setRegistrosIndividuais(atualizados);
      snack.success("Pareceres salvos com sucesso!");
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSaving(false);
    }
  }, [
    matriculas,
    registrosIndividuais,
    textosIndividuais,
    filter.idTurma,
    filter.idMateria,
    filter.etapa,
  ]);

  const handleChangeTextoIndividual = useCallback(
    (idAluno: number, texto: string) =>
      setTextosIndividuais((state) => ({ ...state, [idAluno]: texto })),
    []
  );

  const matriculaAluno =
    filter.idAluno != undefined
      ? matriculas.find((m) => m.aluno?.uid == filter.idAluno)
      : undefined;

  const turmaLabel = turma?.tituloTurma ?? "";
  const tituloCompleto = [
    turmaLabel,
    `${filter.etapa}º ${nomeEtapa}`,
    materia?.nome,
  ]
    .filter((valor) => !!valor)
    .join(" | ");

  const isFilterValid =
    !!filter.idTurma && !!filter.etapa && (tab > 1 || !!filter.idMateria);

  return (
    <Main
      title="Acompanhamento pedagógico descritivo"
      rightbarIcon={<FilterIcon />}
      rightbarChildren={
        <Filter
          Component={Component}
          filter={filter}
          matriculas={matriculas}
          tab={tab}
          handleFilterMatricula={handleFilterMatricula}
        />
      }
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(event, value: number) => setTab(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="1º Momento: Turma" />
          <Tab label="2º Momento: Alunos" />
          <Tab label="Conselho (Turma)" />
          <Tab label="Conselho (Aluno)" />
        </Tabs>
      </Box>

      {!isFilterValid ? (
        <EmptyState
          type="search"
          title="Selecione no filtro"
          subtitle={`Selecione ao menos o ${nomeEtapa} e a Turma no menu lateral direito para iniciar o acompanhamento.`}
        />
      ) : (
        <>
          <TabPanel value={tab} index={0}>
            <AnaliseTurma
              titulo={tituloCompleto}
              texto={textoColetivo}
              loading={loading.coletivo}
              saving={saving}
              onChange={setTextoColetivo}
              onSave={handleSaveColetivo}
            />
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <AnaliseIndividual
              titulo={tituloCompleto}
              matriculas={matriculas}
              textos={textosIndividuais}
              loading={loading.individuais}
              saving={saving}
              onChange={handleChangeTextoIndividual}
              onSave={handleSaveIndividuais}
            />
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <ConselhoTurma
              titulo={[turmaLabel, `${filter.etapa}º ${nomeEtapa}`]
                .filter((valor) => !!valor)
                .join(" | ")}
              pareceres={pareceresTurma}
              loading={loading.turma}
              onImprimir={() => setImpressao("turma")}
            />
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <ConselhoAluno
              titulo={[turmaLabel, `${filter.etapa}º ${nomeEtapa}`]
                .filter((valor) => !!valor)
                .join(" | ")}
              matricula={matriculaAluno}
              pareceres={pareceresAluno}
              loading={loading.aluno}
              onImprimir={() => setImpressao("aluno")}
            />
          </TabPanel>
        </>
      )}

      <Impressao
        open={!!impressao}
        onClose={() => setImpressao(undefined)}
        tipo={impressao}
        escola={escola}
        turma={turmaLabel}
        etapa={filter.etapa}
        nomeEtapa={nomeEtapa}
        matricula={matriculaAluno}
        pareceres={impressao === "aluno" ? pareceresAluno : pareceresTurma}
      />
    </Main>
  );
};
